import React, { useEffect, useState } from 'react';
import { ImageOff, Loader2, X } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

// Las imágenes se piden con el token (no hay URLs públicas) y se guardan como
// blob en memoria mientras la página esté abierta. Máximo CACHE_MAX en caché.
const CACHE_MAX = 60;
const cache = new Map(); // url → objectURL

function remember(url, objectUrl) {
  cache.set(url, objectUrl);
  if (cache.size > CACHE_MAX) {
    const [oldUrl, oldObj] = cache.entries().next().value;
    cache.delete(oldUrl);
    URL.revokeObjectURL(oldObj);
  }
}

/**
 * Imagen protegida del portal (/api/media/... o /api/promotions/.../image).
 * Clic para verla en grande. Si ya no existe, muestra "Imagen ya no disponible".
 */
export default function AuthImage({ url, alt = '', style, maxHeight = 260, zoom = true }) {
  const { apiFetch } = useApi();
  const [src, setSrc] = useState(() => (url ? cache.get(url) : null));
  const [failed, setFailed] = useState(!url);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!url) { setFailed(true); return; }
    if (cache.has(url)) { setSrc(cache.get(url)); setFailed(false); return; }
    let alive = true;
    setFailed(false);
    apiFetch(url, { asFile: true })
      .then(({ blob }) => {
        const obj = URL.createObjectURL(blob);
        remember(url, obj);
        if (alive) setSrc(obj);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [url, apiFetch]);

  if (failed) return <Unavailable />;
  if (!src) {
    return (
      <div style={{ ...st.placeholder, height: Math.min(maxHeight, 160) }}>
        <Loader2 size={18} className="ui-spin" />
      </div>
    );
  }
  return (
    <>
      <img
        src={src} alt={alt}
        style={{ ...st.img, maxHeight, cursor: zoom ? 'zoom-in' : 'default', ...style }}
        onClick={zoom ? () => setOpen(true) : undefined}
      />
      {open && (
        <div style={st.overlay} onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-label="Imagen ampliada">
          <button style={st.close} onClick={() => setOpen(false)} aria-label="Cerrar"><X size={22} /></button>
          <img src={src} alt={alt} style={st.full} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}

export function Unavailable({ text = 'Imagen ya no disponible' }) {
  return (
    <div style={st.unavailable}>
      <ImageOff size={16} /> <span>📷 {text}</span>
    </div>
  );
}

const st = {
  img: { display: 'block', maxWidth: '100%', borderRadius: 10, objectFit: 'contain' },
  placeholder: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 220, maxWidth: '100%', borderRadius: 10, background: 'var(--surface-3)', color: 'var(--text-3)' },
  unavailable: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 10, background: 'var(--surface-3)', color: 'var(--text-3)', fontSize: 12.5 },
  overlay: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' },
  full: { maxWidth: '100%', maxHeight: '100%', borderRadius: 8, cursor: 'default' },
  close: { position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 999, width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
